import React, { useEffect, useMemo, useState } from 'react';
import { connect, getLocale, useIntl, history } from 'umi';
import moment from 'moment';
import { Bubble, Button, Card, ConfigProvider, Layout, Segment, Table } from 'tea-component';
import EventTypeTag from '../EventTypeTag';
import { getEventRecordList, getEventTypeList } from '@/pages/dashboard/services/event';
import { getEventTypeText, getSubtitleDate } from '../_config/utils';
import { useBuriedPoint } from '@/pages/home/utils/customHook';
import { getName } from '@home/utils/common';
import { MENU_CONTROL_PERMISSION, PERIODTIMEOBJ } from '@/pages/dashboard/_config/constant';
import AddEventModal from '../AddEventModal';
import SvgIcon from '@/pages/home/components/SvgIcon';
import DeleteModal from '../DeleteModal';
import { customTagColor, tagTypeArr } from '../_config/constant';
import '@/pages/dashboard/assets/scss/page/Overlay/eventsDetails.scss';

const { Content } = Layout;
const { pageable, autotip } = Table.addons;

function EventDetails(props: any) {
  const { gameCode, roleId } = props;
  const { formatMessage } = useIntl();
  const local = getLocale();
  const isEnLocal = local === "en-US";
  const { query } = history.location;

  // tab页签切换
  const [segment, setSegment] = useState(Number(query.eventType) || '');
  const [eventTypes, setEventTypes] = useState([]);
  // 表格加载
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // const [keyword, setKeyword] = useState(""); // 搜索输入框受控字段
  const [paging, setPaging] = useState({ pageIndex: 1, pageSize: 10 });
  const [total, setTotal] = useState(0);
  const [records, setRecords] = useState([]);
  // 新增事件 弹窗
  const [eventVisible, setEventVisible] = useState(false);
  const [editConfig, setEditConfig] = useState({
    data: [],
    isEdit: false
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteEvent, setDeleteEvent] = useState('');

  // 埋点点信息
  const logObj = {
    pageId: '202201',
    pageName: 'eventAnalysisDetails',
    extInfo: 'eventAnalysisDetails',
    pagePath: '/eventAnalysis/eventDetails',
    uid: gameCode,
    uidType: 'game_code'
  };
  useBuriedPoint(logObj);

  // refresh用于修复编辑相同事件类型下，列表不更新
  useEffect(() => {
    let newSegement = segment;
    if (Number(query.eventType) !== segment) {
      newSegement = Number(query.eventType) || '';
      setSegment(newSegement);
    }
    getTableData(newSegement);
  }, [query?.eventType, query?.refresh]);

  const getStartEndTime = () => {
    const { date, timeType, startDate, endDate, isOverviewAdd } = query;
    if (isOverviewAdd) return [startDate, endDate];
    if (startDate) return [startDate, date];

    // if(timeType === 'Month') {
    //   const start = moment(date).startOf('months').format("YYYY-MM-DD");
    //   return [start, date];
    // } else if (timeType === 'Week') {
    //   const weekOfDay = moment(date).format('E');
    //   const start = moment(date).subtract(Number(weekOfDay) - 1, 'days').format("YYYY-MM-DD");
    //   return [start, date];
    // } else {

    // }
    switch (timeType) {
      case "Month": {
        const start = moment(date).startOf('months').format("YYYY-MM-DD");
        return [start, date];
      }
      case "Week": {
        const weekOfDay = moment(date).format('E');
        const start = moment(date).subtract(Number(weekOfDay) - 1, 'days').format("YYYY-MM-DD");
        return [start, date];
      }
      default: return [date, date];
    }
  };

  // 获取表格数据
  const getTableData = (newSegement = segment, newPaging = paging) => {
    const [start, end] = getStartEndTime();
    const today = moment(Date.now()).format('YYYY-MM-DD');
    const thirtyDays = moment(today).subtract(30, 'day').format('YYYY-MM-DD');
    const params = {
      game_code: gameCode,
      period: PERIODTIMEOBJ[query.timeType] || 'monthly',
      start_time: start || thirtyDays,
      end_time: end || today,
      dashboard_type: query.dashboardType || 'all',
      event_type: newSegement,
      page: newPaging.pageIndex,
      page_size: newPaging.pageSize,
      all_period: (query.dashboardType || 'all') === 'all',
      filters: {
        market_list: ["255"],
        os_list: ["255"],
      }
    };
    setRecords([]);
    setLoading(true);
    setError(null);
    getEventRecordList(params).then((response) => {
      const newTableData = response?.list?.map((item: { event_record: any; event_type: any; have_delete: any; have_edit: any; }) => ({ ...item?.event_record, event_type: item?.event_type, have_delete: item?.have_delete, have_edit: item?.have_edit }));
      setRecords(newTableData || []);
      setTotal(response?.row_count || 0);
    }).catch((e) => {
      setError(e);
      console.log("事件详情页面表格接口错误: ", e);
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    // 请求事件类型列表
    getEventTypeList({ game_code: gameCode }).then((res) => {
      setEventTypes(res);
    }).catch(e => {
      console.log(e);
    });
  }, [gameCode]);

  const getSegmentOptions = (data = []) => {
    if (!Array.isArray(data)) return [];
    return data?.map(item => ({ value: item?.name_en, text: getEventTypeText(item, isEnLocal) }));
  };

  const handleEdit = (record: any) => {
    setEventVisible(true);
    setEditConfig({
      isEdit: true,
      data: record
    });
  };

  const handleAdd = () => {
    setEventVisible(true);
    setEditConfig({
      isEdit: false,
      data: []
    });
  };

  const handleDelete = (record: any) => {
    setShowDeleteModal(true);
    setDeleteEvent(record);
  };

  const columns = useMemo(() => {
    const columns: any[] = [];
    const customArr: string[] = [];
    segment === 0 && columns.push({
      key: "event_type",
      header: formatMessage({ id: "event_type" }),
      width: 160,
      render: (record: { event_type: { name_en: string; }; }) => {
        let customColor = null;
        const name = record?.event_type?.name_en;
        const type = name?.toLowerCase();
        const isCustom = !tagTypeArr?.includes(type);
        if (isCustom) {
          if (customArr?.includes(type)) {
            const colorIndex = customArr.findIndex((item) => item === type);
            customColor = customTagColor?.[colorIndex] || customTagColor?.[customTagColor?.length - 1];
          } else {
            customArr?.push(type);
            customColor = customTagColor?.[customArr?.length - 1] || customTagColor?.[customTagColor?.length - 1];

          }
        }
        return (<EventTypeTag
          customColor={customColor}
          eventType={name}
          isShowCount={false}
          style={{
            minWidth: "60px"
          }}
          title={getEventTypeText(record?.event_type, isEnLocal)}
        >
        </EventTypeTag>);
      }
    });
    columns.push(
      {
        key: "event_date",
        header: formatMessage({ id: "common_date" }),
        width: 220,
        render: (record: any) => {
          const { start_time, end_time } = record;
          const startTime = start_time?.slice(0, 10);
          const endTime = end_time?.slice(0, 10);
          const noHour = start_time?.slice(11, 19) === '00:00:00' && end_time?.slice(11, 19) === '00:00:00';
          const isSigle = startTime === endTime;
          const date = isSigle ? (noHour ? startTime : start_time) : `${startTime  } ~ ${  endTime}`;
          return <>
            <p>{date}</p>
          </>;
        }
      },
      {
        key: "event_title_en",
        // key: isEnLocal ? "event_title_en" : "event_title_cn",
        header: formatMessage({ id: "event_title" }),
        width: 464,
        render: (record: any) => (
          <span>{getName(isEnLocal, record?.event_title_en, record?.event_title_cn) || '-'}</span>
        ),
      },
      {
        key: "countries",
        header: formatMessage({ id: "common_country" }),
        width: 190,
        render: (record: any) => {
          const countryArr = record?.countries?.map((item: any) => item[`country_${isEnLocal ? "en" : "cn"}`]);
          const contryLength = countryArr?.length;
          const countryLenFlag = contryLength > 4;

          const countryRenderArr = countryLenFlag ? countryArr?.slice(0, 4) : countryArr;
          const countryRenderStr = countryRenderArr?.join(", ");

          return countryRenderArr?.length > 0 ? (
            <div className="country-text dbd-event-detail-country">
              {
                countryLenFlag ?

                  <Bubble
                    arrowPointAtCenter
                    placement="top"
                    dark
                    content={countryArr?.join(',')}
                  >
                    {
                      isEnLocal ? `${countryRenderStr},etc.(${contryLength})` :
                        `${countryRenderStr},等${contryLength}个国家`
                    }
                  </Bubble>
                  : countryRenderStr
              }
            </div>
          ) : "-";
        }
      },
      {
        key: "event_desc_en",
        header: formatMessage({ id: "event_description" }),
        width: 295,
        render: (record: any) => (<span>{getName(isEnLocal, record?.event_desc_en, record?.event_desc_cn) || '-'}</span>)
      },
      {
        key: "creator",
        header: formatMessage({ id: "event_creator" }),
        width: 110,
        render: (record: any) => {
          if (record?.creator === "system") return formatMessage({ id: "event_system" });
          return record?.creator || "-";
        }
      },
      {
        key: "event_link_url",
        header: formatMessage({ id: "event_link" }),
        width: 110,
        render: (record: any) => {
          let { event_link_url, event_type, id } = record;
          // id为0代表事件是系统生成, 不是用户手动录入
          // event_type_id为1 代表是Version事件
          if (id === 0 && event_type === 'Version') {
            // event_link_url 前面要拼接用户当前访问的域名
            event_link_url = window.location.origin + event_link_url;
          }
          return <>
            {
              record.event_link_url ? (<span className='dbd-events-detail-link' title={event_link_url} onClick={() => { window.open(event_link_url) }}><Button
                type={"link"}
                className="table-btn"
              >{formatMessage({ id: "event_view_link" })}</Button><SvgIcon className='dbd-events-detail-link-icon' type='other-share-icon' /></span>) : "-"
            }
          </>;
        }
      },
      {
        key: "settings",
        header: formatMessage({ id: 'event_Actions' }),
        width: 135,
        fixed: 'right',
        render: (record: any) => <div className='dbd-event-actions-button'>
          {
            record?.have_edit && <span className='dbd-event-actions-button-link' onClick={() => { handleEdit(record) }} >
              {formatMessage({ id: 'common_edit' })}
            </span>
          }
          {
            record?.have_delete && <span className='dbd-event-actions-button-link' onClick={() => { handleDelete(record) }} >
              {formatMessage({ id: 'common_delete' })}
            </span>
          }
          {
            !record?.have_delete && !record?.have_delete && <span className='tea-text-overflow'>-</span>
          }

        </div>
      },
    );
    return columns;
  }, [segment, query, local]);
  const subtitle = useMemo(() => {
    const { timeType, date, startDate } = query;
    if (startDate) return `${startDate} ~ ${date}`;
    return getSubtitleDate(timeType, date);
  }, [query]);
  return (
    <Content className="events-details">
      <Content.Header
        title={formatMessage({ id: "event_details" })}
        className="events-details-title"
        subtitle={
          <span className="events-details-desc">{subtitle}</span>
        }
        showBackButton
        onBackButtonClick={() => {
          const fromPage = query.fromPage || `/dashboard/${gameCode}/overview/daily`;
          history.push(fromPage);
        }}
      >
      </Content.Header>

      <Content.Body full>
        <div className="details-flex">
          {
            MENU_CONTROL_PERMISSION.includes(roleId) &&
            (<Button
              className="details-flex-btn"
              type="primary"
              onClick={handleAdd}
            >
              {formatMessage({ id: "event_add" })}
            </Button>)
          }

          <Segment
            className="segment-light"
            value={segment}
            onChange={(value) => {
              setPaging({ pageIndex: 1, pageSize: 10 });
              setTotal(0);
              setSegment(value);
              getTableData(value, { pageIndex: 1, pageSize: 10 });
            }}
            options={[
              { value: "", text: formatMessage({ id: "common_all" }) },
              ...getSegmentOptions(eventTypes)
            ]}
          />
        </div>
        <Card>
          <Card.Body>
            <ConfigProvider locale={getLocale() === 'zh-CN' ? 'zh' : "en"}>
              <Table
                bordered={"all"}
                className="details-table details-table-hover"
                records={records}
                columns={columns}
                addons={[
                  autotip({
                    isLoading: loading,
                    isError: !!error,
                    // isFound: total === 0, // 是否显示
                    foundText: formatMessage({ id: "common_not_found" }),
                    loadingText: formatMessage({ id: "common_loading" }), // 加载文案
                    emptyText: formatMessage({ id: "common_nodata" }), // 空数据文案
                    errorText: formatMessage({ id: "common_loading_failed" }),
                    retryText: formatMessage({ id: "common_retry_table" }), // 重试文案
                    // clearResultText: formatMessage({id: "common_go_back_table"}), // 清空结果文案
                    onRetry: () => { getTableData(segment, paging) },
                    // onClear: () => {
                    //   // 搜索结果为空时, 显示返回原列表按钮
                    //   setKeyword("")
                    //   getTableData(true)
                    // },
                    style: { lineHeight: '200px' },
                  }),
                  pageable({
                    recordCount: total,
                    pageIndex: paging.pageIndex,
                    pageSize: paging.pageSize,
                    stateText: total > 10 ? `${total} ${formatMessage({ id: 'common_records' })}` : '',
                    pageSizeOptions: [10, 20, 30, 40, 50],
                    stateTextVisible: total > 10,
                    pageSizeVisible: total > 10,
                    pageIndexVisible: total > 10,
                    jumpVisible: total > 10,
                    endJumpVisible: total > 10,
                    onPagingChange(query) {
                      setPaging({ pageIndex: query.pageIndex, pageSize: query.pageSize });
                      getTableData(segment, { pageIndex: query.pageIndex, pageSize: query.pageSize });
                    }
                  }),
                ]}
              />
            </ConfigProvider>

          </Card.Body>
        </Card>
        <AddEventModal gameCode={gameCode} editConfig={editConfig} visible={eventVisible} onClose={() => setEventVisible(false)} />
        <DeleteModal
          gameCode={gameCode}
          deleteEvent={deleteEvent}
          showDeleteModal={showDeleteModal}
          getTableData={getTableData}
          close={() => { setShowDeleteModal(false) }} />
      </Content.Body>
    </Content>
  );
}

export default connect((state: any) => ({
  gameCode: state.dashboard?.currentGame?.game_code,
  roleId: state.dashboard?.currentGame?.role_id,
}))(EventDetails);

